// src/components/email/EmailCampaignDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';  // ✅ Usar servicio api centralizado
import Swal from 'sweetalert2';
import io from 'socket.io-client';  // ✅ Socket.io para tiempo real

const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : 'https://efit.com.do';

const socket = io(SOCKET_URL, {
  transports: ['websocket'],
  reconnection: true
});

function EmailCampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campana, setCampana] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ejecutando, setEjecutando] = useState(false);
  const [reseteando, setReseteando] = useState(false);
  
  // ✅ Estado para progreso en tiempo real
  const [progreso, setProgreso] = useState({
    total: 0,
    enviados: 0,
    fallidos: 0,
    porcentaje: 0,
    ultimoEmail: '',
    ultimoError: ''
  });

  useEffect(() => {
    cargarDetalle();
    
    // ✅ Suscribirse a eventos de esta campaña
    socket.emit('subscribe:campaign', id);
    
    // ✅ Escuchar progreso en tiempo real
    const handleProgress = (data) => {
      if (data.campaignId === id || !data.campaignId) {
        setProgreso({
          total: data.total || 0,
          enviados: data.enviados || 0,
          fallidos: data.fallidos || 0,
          porcentaje: data.porcentaje || 0,
          ultimoEmail: data.ultimoEmail || '',
          ultimoError: data.ultimoError || ''
        });
        
        // Actualizar estadísticas en tiempo real
        setEstadisticas(prev => ({
          ...prev,
          total: data.total || prev?.total || 0,
          enviados: data.enviados || prev?.enviados || 0,
          fallidos: data.fallidos || prev?.fallidos || 0,
          tasa_entrega: data.total > 0 
            ? ((data.enviados / data.total) * 100).toFixed(2) 
            : prev?.tasa_entrega || 0
        }));
      }
    };
    
    // ✅ Escuchar cuando se completa
    const handleComplete = (data) => {
      if (data.campaignId === id || !data.campaignId) {
        setEjecutando(false);
        setProgreso(prev => ({ ...prev, porcentaje: 100 }));
        
        Swal.fire({
          icon: 'success',
          title: '✅ Campaña Completada',
          html: `
            <p><strong>Total:</strong> ${data.total}</p>
            <p><strong>Enviados:</strong> ${data.enviados}</p>
            <p><strong>Fallidos:</strong> ${data.fallidos}</p>
          `,
          timer: 3000
        });
        
        cargarDetalle();
      }
    };
    
    // ✅ Escuchar errores
    const handleError = (data) => {
      if (data.campaignId === id || !data.campaignId) {
        setEjecutando(false);
        Swal.fire({
          icon: 'error',
          title: '❌ Error en Campaña',
          text: data.error || 'Error desconocido'
        });
      }
    };
    
    socket.on('campaign:progress', handleProgress);
    socket.on('campaign:complete', handleComplete);
    socket.on('campaign:error', handleError);
    
    // ✅ Cleanup al desmontar
    return () => {
      socket.off('campaign:progress', handleProgress);
      socket.off('campaign:complete', handleComplete);
      socket.off('campaign:error', handleError);
    };
  }, [id]);

  const cargarDetalle = async () => {
    try {
      // ✅ Usar api en lugar de axios
      const [campanaRes, estadisticasRes] = await Promise.all([
        api.get(`/email/campanas/${id}`),
        api.get(`/email/campanas/${id}/estadisticas`)
      ]);
      
      setCampana(campanaRes.data.campana);
      setEstadisticas(estadisticasRes.data.estadisticas);
      setProgreso({
        total: estadisticasRes.data.estadisticas.total || 0,
        enviados: estadisticasRes.data.estadisticas.enviados || 0,
        fallidos: estadisticasRes.data.estadisticas.fallidos || 0,
        porcentaje: 0,
        ultimoEmail: '',
        ultimoError: ''
      });
    } catch (error) {
      console.error('Error cargando detalle:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar el detalle de la campaña'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEjecutar = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: '¿Ejecutar Campaña?',
      html: `<p>¿Estás listo para enviar <strong>${campana.nombre}</strong>?</p>` +
            `<p style="color: #64748b; font-size: 14px;">📧 Se enviarán <strong>${campana.total_destinatarios || 'X'}</strong> emails</p>` +
            `<p style="color: #ef4444; font-size: 12px;">⚠️ Esta acción no se puede deshacer</p>`,
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#64748b',
      confirmButtonText: '🚀 Sí, Enviar Ahora',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      setEjecutando(true);
      
      // Resetear progreso
      setProgreso({
        total: campana.total_destinatarios,
        enviados: 0,
        fallidos: 0,
        porcentaje: 0,
        ultimoEmail: '',
        ultimoError: ''
      });
      
      // ✅ Usar api en lugar de axios
      await api.post(`/email/campanas/${id}/ejecutar`);
      
    } catch (error) {
      console.error('Error ejecutando campaña:', error);
      setEjecutando(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo ejecutar la campaña'
      });
    }
  };

  const handleResetear = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Resetear Estado?',
      html: `<p>¿Deseas resetear la campaña <strong>${campana.nombre}</strong> a estado <strong>borrador</strong>?</p>` +
            `<p style="color: #64748b; font-size: 14px;">🔄 Esto te permitirá volver a ejecutarla</p>` +
            `<p style="color: #f59e0b; font-size: 12px;">⚠️ Los logs anteriores se mantendrán</p>`,
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#64748b',
      confirmButtonText: '🔄 Sí, Resetear',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      setReseteando(true);
      await api.post(`/email/campanas/${id}/resetear-estado`, { 
        nuevoEstado: 'borrador' 
      });

      Swal.fire({
        icon: 'success',
        title: '✅ Estado Resetado',
        text: 'La campaña ahora está en borrador y puede ejecutarse nuevamente',
        timer: 2000,
        showConfirmButton: false
      });

      cargarDetalle();
    } catch (error) {
      console.error('Error reseteando campaña:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo resetear el estado'
      });
    } finally {
      setReseteando(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Cargando detalle de la campaña...</div>
      </div>
    );
  }

  if (!campana) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>❌ Campaña no encontrada</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{campana.nombre}</h1>
        <button onClick={() => navigate('/email')} style={styles.btnSecondary}>
          ← Volver
        </button>
      </div>

      {/* ✅ BARRA DE PROGRESO EN TIEMPO REAL */}
      {ejecutando && (
        <div style={styles.progressCard}>
          <h3 style={styles.progressTitle}>📊 Progreso en Tiempo Real</h3>
          
          {/* Barra de progreso */}
          <div style={styles.progressBar}>
            <div style={{
              ...styles.progressFill,
              width: `${progreso.porcentaje}%`,
              backgroundColor: progreso.porcentaje === 100 ? '#10b981' : '#3b82f6'
            }} />
          </div>
          
          {/* Estadísticas en vivo */}
          <div style={styles.progressStats}>
            <div style={styles.progressStat}>
              <span style={styles.progressStatLabel}>Total</span>
              <span style={styles.progressStatValue}>{progreso.total}</span>
            </div>
            <div style={styles.progressStat}>
              <span style={styles.progressStatLabel}>✅ Enviados</span>
              <span style={{...styles.progressStatValue, color: '#10b981'}}>{progreso.enviados}</span>
            </div>
            <div style={styles.progressStat}>
              <span style={styles.progressStatLabel}>❌ Fallidos</span>
              <span style={{...styles.progressStatValue, color: '#ef4444'}}>{progreso.fallidos}</span>
            </div>
            <div style={styles.progressStat}>
              <span style={styles.progressStatLabel}>Progreso</span>
              <span style={{...styles.progressStatValue, color: '#3b82f6'}}>{progreso.porcentaje}%</span>
            </div>
          </div>
          
          {/* Último email enviado */}
          {progreso.ultimoEmail && (
            <div style={styles.progressLastEmail}>
              📧 Último: {progreso.ultimoEmail}
            </div>
          )}
          
          {/* Último error */}
          {progreso.ultimoError && (
            <div style={styles.progressLastError}>
              ⚠️ Error: {progreso.ultimoError}
            </div>
          )}
        </div>
      )}

      {/* Info de la campaña */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Información de la Campaña</h2>
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Plantilla</span>
            <span style={styles.infoValue}>{campana.plantilla?.nombre || 'N/A'}</span>
          </div>
          
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Estado</span>
            <span style={{
              ...styles.badge,
              ...(campana.estado === 'completado' ? styles.badgeSuccess :
                campana.estado === 'enviando' ? styles.badgeInfo :
                campana.estado === 'borrador' ? styles.badgeWarning :
                campana.estado === 'eliminado' ? styles.badgeSecondary :
                campana.estado === 'error' ? styles.badgeDanger :
                styles.badgeInfo)
            }}>
              {campana.estado}
            </span>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Grupo de Destinatarios</span>
            <span style={styles.infoValue}>
              {campana.grupo?.nombre || (campana.EmailGrupoId ? `ID: ${campana.EmailGrupoId}` : 'No asignado')}
            </span>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Tipo Destinatarios</span>
            <span style={styles.infoValue}>
              {campana.tipo_destinatarios === 'grupo' ? '👥 Grupo' :
               campana.tipo_destinatarios === 'csv' ? '📎 CSV' :
               campana.tipo_destinatarios === 'todos' ? '🌐 Todos los Clientes' :
               'N/A'}
            </span>
          </div>
          
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Fecha de Creación</span>
            <span style={styles.infoValue}>{new Date(campana.created_at).toLocaleString()}</span>
          </div>
          
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Iniciado Por</span>
            <span style={styles.infoValue}>Usuario ID: {campana.iniciado_por || 'N/A'}</span>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Total Destinatarios</span>
            <span style={styles.infoValue}>{campana.total_destinatarios || 0}</span>
          </div>
        </div>

        {/* Botón Ejecutar */}
        {campana.estado === 'borrador' && campana.EmailGrupoId && (
          <div style={styles.actionSection}>
            <button 
              onClick={handleEjecutar} 
              style={styles.btnEjecutar}
              disabled={ejecutando}
            >
              {ejecutando ? '⏳ Enviando...' : '🚀 Ejecutar Campaña'}
            </button>
            <p style={styles.hint}>
              📧 Se enviarán a los {campana.total_destinatarios || 'X'} destinatarios del grupo
            </p>
          </div>
        )}

        {/* Botón Resetear */}
        {campana.estado === 'error' && (
          <div style={styles.errorSection}>
            <div style={styles.errorBox}>
              <p style={styles.errorTitle}>⚠️ <strong>Campaña Fallida</strong></p>
              <p style={styles.errorMessage}>
                {campana.error_message || 'Error desconocido al ejecutar la campaña'}
              </p>
            </div>
            <button 
              onClick={handleResetear} 
              style={styles.btnResetear}
              disabled={reseteando}
            >
              {reseteando ? '⏳ Resetando...' : '🔄 Resetear a Borrador'}
            </button>
            <p style={styles.hint}>
              🔄 Esto permitirá volver a ejecutar la campaña después de corregir el error
            </p>
          </div>
        )}

        {campana.estado === 'borrador' && !campana.EmailGrupoId && (
          <div style={styles.warningBox}>
            ⚠️ <strong>Atención:</strong> Esta campaña no tiene grupo asignado. 
            <br />
            <button 
              onClick={() => navigate('/email/campanas/nueva')} 
              style={styles.linkButton}
            >
              → Crear nueva campaña
            </button>
          </div>
        )}
      </div>

      {/* Estadísticas */}
      {estadisticas && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Estadísticas de Envío</h2>
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Total</div>
              <div style={{ ...styles.statValue, color: '#3b82f6' }}>{estadisticas.total || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Enviados</div>
              <div style={{ ...styles.statValue, color: '#10b981' }}>{estadisticas.enviados || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Fallidos</div>
              <div style={{ ...styles.statValue, color: '#ef4444' }}>{estadisticas.fallidos || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Tasa de Entrega</div>
              <div style={{ ...styles.statValue, color: '#8b5cf6' }}>{estadisticas.tasa_entrega || 0}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Logs de envío */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Registro de Envíos</h2>
        <div style={styles.tableContainer}>
          {(!campana.logs || campana.logs.length === 0) ? (
            <div style={styles.emptyLogs}>
              <p style={styles.emptyLogsIcon}>📋</p>
              <p style={styles.emptyLogsText}>
                {campana.estado === 'borrador' ? 'Campaña en borrador - Sin envíos registrados' :
                 campana.estado === 'eliminado' ? 'Campaña eliminada' :
                 campana.estado === 'error' ? 'Campaña falló antes de completar los envíos' :
                 'No hay registros de envío'}
              </p>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Fecha Envío</th>
                  <th style={styles.th}>Error</th>
                </tr>
              </thead>
              <tbody>
                {(campana.logs || []).slice(0, 50).map(log => (
                  <tr key={log.id} style={styles.tr}>
                    <td style={styles.td}>{log.email}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        ...(log.estado === 'enviado' ? styles.badgeSuccess :
                          log.estado === 'fallido' ? styles.badgeDanger :
                          styles.badgeInfo)
                      }}>
                        {log.estado}
                      </span>
                    </td>
                    <td style={styles.td}>{log.enviado_at ? new Date(log.enviado_at).toLocaleString() : 'N/A'}</td>
                    <td style={{ ...styles.td, color: '#ef4444' }}>{log.error_message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: 'calc(100vh - 70px)',
    background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
    padding: '24px 32px',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b'
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#ef4444'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  btnSecondary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e5e7eb'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  infoItem: { 
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  infoLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  infoValue: {
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: '600'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px'
  },
  statCard: {
    padding: '20px',
    background: '#f9fafb',
    borderRadius: '8px',
    textAlign: 'center'
  },
  statLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '8px'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700'
  },
  tableContainer: {
    overflow: 'hidden',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #e5e7eb',
    color: '#374151'
  },
  tr: {
    transition: 'background 0.2s'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '20px',
    textTransform: 'uppercase'
  },
  badgeSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  badgeDanger: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  badgeInfo: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },
  badgeWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  badgeSecondary: {
    backgroundColor: '#e5e7eb',
    color: '#374151'
  },
  actionSection: {
    marginTop: '24px',
    padding: '20px',
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    borderRadius: '8px',
    border: '2px solid #3b82f6',
    textAlign: 'center'
  },
  btnEjecutar: {
    padding: '14px 32px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s'
  },
  errorSection: {
    marginTop: '24px',
    padding: '20px',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    borderRadius: '8px',
    border: '2px solid #f59e0b',
    textAlign: 'center'
  },
  errorBox: {
    marginBottom: '16px',
    padding: '12px',
    background: '#fef3c7',
    borderRadius: '6px',
    border: '1px solid #f59e0b'
  },
  errorTitle: {
    margin: '0 0 8px 0',
    color: '#92400e',
    fontSize: '16px'
  },
  errorMessage: {
    margin: 0,
    color: '#78350f',
    fontSize: '13px',
    fontFamily: 'monospace',
    wordBreak: 'break-word'
  },
  btnResetear: {
    padding: '14px 32px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
    transition: 'all 0.2s'
  },
  hint: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  },
  warningBox: {
    marginTop: '24px',
    padding: '16px',
    background: '#fef3c7',
    borderRadius: '8px',
    border: '1px solid #f59e0b',
    color: '#92400e',
    fontSize: '14px',
    lineHeight: '1.8'
  },
  linkButton: {
    display: 'inline-block',
    marginTop: '12px',
    color: '#0369a1',
    fontWeight: '600',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    textDecoration: 'underline'
  },
  emptyLogs: {
    textAlign: 'center',
    padding: '40px 20px'
  },
  emptyLogsIcon: {
    fontSize: '48px',
    marginBottom: '12px',
    opacity: 0.5
  },
  emptyLogsText: {
    fontSize: '16px',
    color: '#64748b',
    marginBottom: '8px'
  },
  emptyLogsHint: {
    fontSize: '13px',
    color: '#94a3b8'
  },
  // ✅ ESTILOS PARA BARRA DE PROGRESO
  progressCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
    border: '2px solid #3b82f6'
  },
  progressTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '16px'
  },
  progressBar: {
    width: '100%',
    height: '24px',
    backgroundColor: '#e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '16px'
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease',
    borderRadius: '12px'
  },
  progressStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '12px'
  },
  progressStat: {
    textAlign: 'center',
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '8px'
  },
  progressStatLabel: {
    fontSize: '12px',
    color: '#64748b',
    display: 'block',
    marginBottom: '4px'
  },
  progressStatValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b'
  },
  progressLastEmail: {
    fontSize: '13px',
    color: '#10b981',
    padding: '8px 12px',
    background: '#d1fae5',
    borderRadius: '6px',
    marginBottom: '8px'
  },
  progressLastError: {
    fontSize: '13px',
    color: '#ef4444',
    padding: '8px 12px',
    background: '#fee2e2',
    borderRadius: '6px'
  }
};

export default EmailCampaignDetail;